import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface StepProgressProps {
  currentStep: number;
  totalSteps: number;
  stepLabels?: string[];
}

const StepProgress = ({ currentStep, totalSteps, stepLabels }: StepProgressProps) => {
  return (
    <div className="flex items-center gap-2 w-full px-4 sm:px-8">
      {Array.from({ length: totalSteps }).map((_, index) => (
        <React.Fragment key={index}>
          <div className="flex flex-col items-center gap-1.5">
            <motion.div
              className={cn(
                "w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 shadow-sm",
                index < currentStep 
                  ? "bg-green-500 text-white"
                  : index === currentStep 
                    ? "bg-brand-500 text-white ring-4 ring-brand-200 dark:ring-brand-800"
                    : "bg-muted text-muted-foreground"
              )}
              initial={false}
              animate={{
                scale: index === currentStep ? 1.1 : 1,
                backgroundColor: index < currentStep 
                  ? "rgb(34, 197, 94)" 
                  : index === currentStep 
                    ? "hsl(var(--brand-500))" 
                    : "hsl(var(--muted))"
              }}
              transition={{ type: "spring", stiffness: 400, damping: 25 }}
            >
              {index < currentStep ? (
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                >
                  <Check className="h-5 w-5" />
                </motion.div>
              ) : (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 }}
                >
                  {index + 1}
                </motion.span>
              )}
            </motion.div>
            {/* Step label - visible on larger screens */}
            {stepLabels && stepLabels[index] && (
              <motion.span 
                className={cn(
                  "text-[10px] sm:text-xs font-medium text-center hidden sm:block max-w-[60px] leading-tight",
                  index === currentStep 
                    ? "text-brand-600 dark:text-brand-400" 
                    : index < currentStep 
                      ? "text-green-600 dark:text-green-400"
                      : "text-muted-foreground"
                )}
                initial={{ opacity: 0, y: 4 }}
                animate={{ 
                  opacity: 1, 
                  y: 0,
                  scale: index === currentStep ? 1.05 : 1
                }}
                transition={{ 
                  delay: index * 0.08, 
                  duration: 0.3,
                  ease: "easeOut"
                }}
              >
                {stepLabels[index]}
              </motion.span>
            )}
          </div>
          
          {index < totalSteps - 1 && (
            <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden self-start mt-4 sm:mt-[18px]">
              <motion.div 
                className="h-full bg-green-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ 
                  width: index < currentStep ? "100%" : "0%" 
                }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default StepProgress;
